import * as mapModules from '../../map/index.js';
import { LayerSettings } from "../../map/layer/utils/LayerSettings.js";
import { ActiveFilters } from "../../map/layer/utils/ActiveFilters.js";
import { GeoJSONLayer } from '../../map/layer/GeoJSONLayer.js';
import { saveAsFile } from '../../utils/utils.js';
import { Layer } from '../../map/layer/Layer.js';
import { Folder } from '../../map/layer/Folder.js';

/**
 * Loader for the LayerTree module
 * 
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @author rhess <robin.hess@awi.de> 
 */

/**
 * A function that loads the LayerTree
 * 
 * @memberof vef.viewer.loader
 * 
 * @param {object} options 
 * @param {Viewer} viewer 
 */
export function LayerTree(options, viewer) {
    const tree = new mapModules.LayerTree(null, viewer.layerCache, options);
    const map = viewer.elements[options.targetMap];

    initLayerEvents(tree, viewer, options);
    initZoomLayer(tree, map);
    initLayerSettings(tree);
    initLayerInfo(tree, viewer, options);
    initSelectEvents(tree, map);
    initShowFilters(tree, viewer.filters);
    initDownloadEvent(tree);
    initMapEvents(map, tree);

    viewer.addElement(options.id, tree);
};

function initSelectEvents(tree, map) {

    const selectLayer = item => {
        if (item.layer instanceof Folder) return;
        map.addLayer(item.layer);
        item.layer.active = true;
        item.layer.fire("enable", item.layer);
    }

    const deselectLayer = item => {
        if (item.layer instanceof Folder) return;
        map.removeLayer(item.layer);
        item.layer.active = false;
        tree.hideLoadingIcon(item.layer.uniqueId);
        item.layer.fire("disable", item.layer);
    }

    tree.on('select_node', (e) => selectLayer(e.detail, map));
    tree.on('deselect_node', (e) => deselectLayer(e.detail, map));

    // select active layers
    const items = tree.getSelectedItems();
    for (let i = 0; i < items.length; ++i) {
        selectLayer(items[i], map);
    }
}

function initLayerEvents(tree, viewer, options) {
    const initLayerEvents = layer => {
        // layer loading icon
        if (layer instanceof Layer) {
            layer.on('loading', () => tree.showLoadingIcon(layer.uniqueId));
            layer.on('load', () => tree.hideLoadingIcon(layer.uniqueId));
        }
    }

    // call for each layer and for newly added layers
    viewer.layerCache.forEach(layer => initLayerEvents(layer));
    tree.on('add_layer', e => initLayerEvents(e.detail.layer));
}

function initZoomLayer(tree, map) {
    tree.on('zoom_layer', (e) => {
        const layer = e.detail.layer;
        map.fitBounds(layer.getBounds());
    });
}

function initLayerInfo(tree, viewer, options) {
    if (!options.targetSidebar || !options.infoSidebarGroup) return;

    const sidebar = viewer.elements[options.targetSidebar];
    let currentLayer, currentGroup;

    sidebar.on("show", group => { currentGroup = group; });
    sidebar.on("close", () => { currentGroup = null; });

    const updateSidebarContent = () => {
        sidebar.setContent(options.infoSidebarGroup, currentLayer.printMetadata());
        sidebar.setTitle(options.infoSidebarGroup, currentLayer.title);
    };

    tree.on('show_info', (e) => {
        currentLayer = e.detail.layer;
        updateSidebarContent();
        sidebar.show(options.infoSidebarGroup);
    });

    tree.on('title_change', (e) => {
        if ((currentGroup === options.infoSidebarGroup) && (currentLayer === e.detail.layer)) {
            updateSidebarContent();
        }
    });
}

function initLayerSettings(tree) {
    let layerSettings = null;
    tree.on('show_settings', function (e) {
        if (!layerSettings) layerSettings = new LayerSettings();
        layerSettings.setOptions({ pointerEvent: e.detail.pointerEvent });
        layerSettings.open(e.detail.layer);
    });
}

/**
 * Show information for active filter values for each layer
 *  
 * @param {*} tree 
 * @param {*} filters
 */
function initShowFilters(tree, filters) {
    let activeFilters = null;
    tree.on('active_filters', function (e) {
        if (!activeFilters) activeFilters = new ActiveFilters();
        activeFilters.setOptions({ pointerEvent: e.detail.pointerEvent });
        activeFilters.open(e.detail.layer);
    });
    filters.on("change", () => {
        if (activeFilters) activeFilters.redrawDimensions(filters.getActiveFilters());
    })
}

/**
 * initialize the download event
 */
function initDownloadEvent(tree) {
    tree.on('download_layer', e => {
        if (e.detail.layer instanceof GeoJSONLayer) {
            e.detail.layer.exportGeometry("download");
        }
    })

    tree.on('download_folder', e => {
        const features = [];
        const children = e.detail.element.querySelectorAll('.list-item');
        for (let i = 0; i < children.length; ++i) {
            const id = children[i].dataset.id;
            const layer = tree.getItem(id).layer;
            if (layer instanceof GeoJSONLayer) {
                features.push(layer.exportGeometry("object"));
            }
        }

        const json = JSON.stringify({
            type: "FeatureCollection",
            features: features
        });
        const blob = new Blob([json], { type: "text/plain;charset=utf-8" });
        saveAsFile(blob, e.detail.layer.title + ".json");
    })
}

/**
 * Init events on the map (e.g. adding drawn layers)
 */
function initMapEvents(map, tree) {
    map.on("create_layer", layer => {
        tree.addLayer("#", layer);
        tree.selectNodes(layer.uniqueId);
    });

    map.on("projection_change", e => checkLayerProjections(tree, e.crs));
    tree.on("add_layer", e => {
        if (!e.detail.layer.availableCrs.includes(map.options.crs)) {
            tree.deselectNodes(e.detail.id);
            tree.disableNodes(e.detail.id);
        }
    });

    checkLayerProjections(tree, map.options.crs);
}

function checkLayerProjections(tree, crs) {
    const items = tree.getItems();
    for (let i = 0; i < items.length; ++i) {
        const node = items[i].element;
        const layer = items[i].layer;
        if (layer.availableCrs && node.getAttribute("role") == "layer") {
            const selected = (node.getAttribute("selected") == "true") ? true : false;
            const disabled = (node.getAttribute("disabled") == "true") ? true : false;
            if (layer.availableCrs.includes(crs)) {
                if (disabled) tree.enableNodes(node);
            } else {
                if (selected) tree.deselectNodes(node);
                if (!disabled) tree.disableNodes(node);
            }
        }
    }
}